import { Box, useMediaQuery, useTheme } from '@mui/material';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, UseFormSetError, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import {
	getChangesHistory,
	loadUseCases,
	loadUserStories,
	loadUserStoryTest,
	updateTicket,
} from 'store/ticket-service/asyncActions';
import {
	getActiveStep,
	getActiveSubStage,
	getActiveTicket,
	getDetailedViewTicketSummary,
	getIsChatOpen,
	getIsTicketShare,
	getIsTicketUpdate,
	getTickets,
} from 'store/ticket-service/selector';
import {
	setActiveStep,
	setActiveSubStage,
	setActiveTicket,
	setDetailedViewTicketSummary,
	setEditMode,
	setIsTicketUpdate,
	updateLocalTicket,
} from 'store/ticket-service/ticketSlice';
import { TicketType } from 'store/ticket-service/types';
import { getTicketSaveTrigger } from 'store/user-service/selector';
import { resetTicketSave } from 'store/user-service/userSlice';

import { useEventBus } from 'utils/EventBus';

import {
	GeneralStepConversationTopics,
	MessageActionStates,
	MessageActionTypes,
	MessageSender,
	NamespacesList,
	SharePermissions,
	TicketStages,
} from 'core/enums/common';
import { successNotify } from 'core/helpers/toasts';

import { ContentType, FormTitleValue } from 'pages/Home';

import { Chatbot } from 'components/Chatbot';
import { IMessage } from 'components/Chatbot/Message/types';
import Loader from 'components/Loader/Loader';
import { GeneralStep } from 'components/TicketSteps/GeneralStep';
import { NotificationStep } from 'components/TicketSteps/NotificationStep';
import ReportStep from 'components/TicketSteps/ReportStep';
import { TestCasesStep } from 'components/TicketSteps/TestCasesStep';
import { UsecasesStep } from 'components/TicketSteps/UsecasesStep';

interface FormValues {
	title: string;
	description: string;
	messages: IMessage[];
	summary: string;
}

interface CreateRequestProps {
	contentView: ContentType;
	ticketTitle: string;
	setTitleError: UseFormSetError<FormTitleValue>;
}

export const Ticket: FC<CreateRequestProps> = ({ contentView, ticketTitle, setTitleError }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	const { t: commonTranslate } = useTranslation(NamespacesList['common']);

	const dispatch = useAppDispatch();
	const { addEventListener, removeEventListener, dispatchEvent } = useEventBus();

	const activeTicket = useSelector(getActiveTicket);
	const tickets = useSelector(getTickets);
	const activeStep = useSelector(getActiveStep);
	const activeSubStage = useSelector(getActiveSubStage);
	const isTicketUpdate = useSelector(getIsTicketUpdate);
	const ticketSaveTrigger = useSelector(getTicketSaveTrigger);
	
	// Référence pour stocker l'ID du ticket précédent et éviter les boucles infinies
	const previousTicketIdRef = useRef<string | undefined>(undefined);
	const isChatOpen = useSelector(getIsChatOpen);
	const detailedViewTicketSummary = useSelector(getDetailedViewTicketSummary);
	const isTicketShared = useSelector(getIsTicketShare);

	const methods = useForm<FormValues>({
		defaultValues: {
			title: '',
			description: '',
			messages: [],
			summary: '',
		},
	});

	const { reset } = methods;

	useEffect(() => {
		if (activeTicket) {
			reset({
				messages:
					[...activeTicket.messages].sort(
						(a, b: IMessage) => new Date(a.sendTime).getTime() - new Date(b.sendTime).getTime()
					) || [],
			});
		}
	}, [activeTicket]);

	const [messageValue, setMessageValue] = useState<string>('');
	const [messages, setMessages] = useState<Array<IMessage>>([]);
	const [isChatLoading, setIsChatLoading] = useState<boolean>(false);

	const [isTicketLoading, setIsTicketLoading] = useState<boolean>(true);

	useEffect(() => {
		addEventListener('messageSend', clearMessageField);
		addEventListener('saveTicket', handleTicketSave);

		return () => {
			removeEventListener('messageSend', clearMessageField);
			removeEventListener('saveTicket', handleTicketSave);
		};
	}, [activeTicket?.id, isTicketUpdate, activeTicket]);

	useEffect(() => {
		if (messageValue) {
			clearMessageField();
		}
	}, [activeTicket?.id, activeStep, activeSubStage]);

	const isRequestUserStoriesOnSharedTicket = () => {
		const permissions = activeTicket?.permissions?.length
			? activeTicket.permissions.map((permission) => permission.ticketCurrentStep)
			: [];

		return (
			permissions.includes(null) ||
			permissions.includes(TicketStages.general) ||
			permissions.includes(TicketStages.useCases) ||
			permissions.includes(TicketStages.report)
		);
	};

	const isRequestUseCasesOnSharedTicket = () => {
		const permissions = activeTicket?.permissions?.length
			? activeTicket.permissions.map((permission) => permission.ticketCurrentStep)
			: [];

		return (
			permissions.includes(null) ||
			permissions.includes(TicketStages.useCases) ||
			permissions.includes(TicketStages.testCases) ||
			permissions.includes(TicketStages.report)
		);
	};

	const isRequestTestCasesOnSharedTicket = () => {
		const permissions = activeTicket?.permissions?.length
			? activeTicket.permissions.map((permission) => permission.ticketCurrentStep)
			: [];

		return (
			permissions.includes(null) ||
			permissions.includes(TicketStages.testCases) ||
			permissions.includes(TicketStages.report)
		);
	};

	// setting initial data for ticket
	const handleLoadTicket = async () => {
		if (!activeTicket?.id) return;
		
		// Vérifier si c'est le premier montage du composant ou si le ticket a vraiment changé
		const isFirstMount = previousTicketIdRef.current === undefined;
		const isTicketChanged = !isFirstMount && previousTicketIdRef.current !== activeTicket.id;
		
		try {
			setIsTicketLoading(true);

			if (activeTicket.messages) {
				setMessages(() => [...activeTicket.messages]);
			}

			dispatch(setEditMode(false));
			
			// Ne réinitialiser l'étape et la sous-étape que si le ticket a changé (pas au premier montage)
			// Cela permet de conserver l'étape sélectionnée dans le Stepper lors de la navigation depuis Support/Settings
			if (isTicketChanged) {
				dispatch(setActiveStep(activeTicket?.currentStep || 0));
				dispatch(setActiveSubStage(GeneralStepConversationTopics['Functional requirements']));
			}

			dispatch(getChangesHistory({ ticketId: activeTicket.id, ticketStep: TicketStages.general }));

			if (
				isTicketShared
					? isRequestUserStoriesOnSharedTicket()
					: !activeTicket?.ticketSummaries?.length
			) {
				const activeTicketUserStories = await dispatch(
					loadUserStories({ ticketId: activeTicket.id })
				).unwrap();

				if (isTicketShared ? isRequestUseCasesOnSharedTicket() : activeTicketUserStories) {
					await dispatch(loadUseCases({ ticketId: activeTicket.id }));
				}
			}

			if (
				isTicketShared ? isRequestTestCasesOnSharedTicket() : !activeTicket?.userStoryTests?.length
			) {
				await dispatch(loadUserStoryTest({ ticketId: activeTicket.id }));
			}
		} catch (e) {
			console.error('ERROR DURING TICKET LOADING', e);
		} finally {
			setIsTicketLoading(false);
			// Mettre à jour la référence pour le prochain appel
			previousTicketIdRef.current = activeTicket.id;
		}
	};

	useEffect(() => {
		handleLoadTicket();
	}, [activeTicket?.id]);

	const description = useMemo(() => {
		return activeTicket?.description || '';
	}, [activeTicket?.description]);

	const stageMessages = useMemo(() => {
		return messages.filter((message: IMessage) => {
			if (activeStep === TicketStages['general']) {
				return (
					message.stage === activeStep &&
					message.subStage === activeSubStage &&
					message.sender !== MessageSender.hidden
				);
			} else {
				return message.stage === activeStep && message.sender !== MessageSender.hidden;
			}
		});
	}, [messages, activeStep, activeSubStage]);

	// isWaitingProceeding - is used to disable chat input if user has not accepted nor declined system message

	const isWaitingProceeding = useMemo(() => {
		const waitingMessages = stageMessages.filter(
			(message) =>
				message.action?.type === MessageActionTypes.generate &&
				message.action?.state === MessageActionStates.default
		);

		return Boolean(waitingMessages.length);
	}, [stageMessages]);

	const clearMessageField = () => {
		setMessageValue('');
	};

	useEffect(() => {
		if (activeTicket) {
			const updatedTicket = tickets.find((ticket: TicketType) => ticket.id === activeTicket.id);
			if (updatedTicket) {
				// Ne mettre à jour que si c'est un nouveau ticket (évite les boucles infinies)
				const isNewTicket = previousTicketIdRef.current !== activeTicket.id;
				
				if (isNewTicket) {
					previousTicketIdRef.current = activeTicket.id;
					
					// Préserver les données importantes de activeTicket (ticketSummaries, useCases, userStoryTests)
					// qui pourraient ne pas être dans le ticket du tableau ou qui sont plus récentes
					const mergedTicket = {
						...updatedTicket,
						// Préserver ticketSummaries de activeTicket si elles existent (elles sont plus récentes)
						ticketSummaries: activeTicket.ticketSummaries?.length
							? activeTicket.ticketSummaries
							: updatedTicket.ticketSummaries || [],
						// Préserver useCases de activeTicket si elles existent (elles sont plus récentes)
						useCases: activeTicket.useCases?.length
							? activeTicket.useCases
							: updatedTicket.useCases || [],
						// Préserver userStoryTests de activeTicket si elles existent (elles sont plus récentes)
						userStoryTests: activeTicket.userStoryTests?.length
							? activeTicket.userStoryTests
							: updatedTicket.userStoryTests || [],
					};
					
					dispatch(setActiveTicket(mergedTicket));
				} else {
					// Pour un ticket existant, vérifier si les données importantes doivent être préservées
					// seulement si le ticket du tableau n'a pas ces données mais activeTicket les a
					const needsPreservation = 
						(activeTicket.ticketSummaries?.length && !updatedTicket.ticketSummaries?.length) ||
						(activeTicket.useCases?.length && !updatedTicket.useCases?.length) ||
						(activeTicket.userStoryTests?.length && !updatedTicket.userStoryTests?.length);
					
					if (needsPreservation) {
						const mergedTicket = {
							...updatedTicket,
							ticketSummaries: activeTicket.ticketSummaries?.length
								? activeTicket.ticketSummaries
								: updatedTicket.ticketSummaries || [],
							useCases: activeTicket.useCases?.length
								? activeTicket.useCases
								: updatedTicket.useCases || [],
							userStoryTests: activeTicket.userStoryTests?.length
								? activeTicket.userStoryTests
								: updatedTicket.userStoryTests || [],
						};
						dispatch(setActiveTicket(mergedTicket));
					}
				}
			}
		} else {
			previousTicketIdRef.current = undefined;
		}
	}, [tickets, activeTicket?.id, dispatch]);
	
 
	
	useEffect(() => {
		if (activeTicket?.id && messages.length)
			dispatch(updateLocalTicket({ id: activeTicket.id, messages }));
	}, [messages]);

	useEffect(() => {
		if (activeTicket?.id)
			dispatch(updateLocalTicket({ id: activeTicket.id, currentStep: activeStep }));
	}, [activeStep]);

	useEffect(() => {
		if (ticketSaveTrigger) handleTicketSave();
	}, [ticketSaveTrigger]);

	useEffect(() => {
		dispatch(setDetailedViewTicketSummary(null));
	}, [activeTicket?.id, activeSubStage, activeStep]);

	const isContentFullWidth = useMemo(
		() =>
			activeStep === TicketStages.report ||
			activeStep === TicketStages.useCases ||
			activeStep === TicketStages.testCases ||
			!isChatOpen ||
			detailedViewTicketSummary,
		[activeStep, isChatOpen, detailedViewTicketSummary]
	);

	const isChatWindowOpen = useMemo(
		() =>
			activeStep !== TicketStages.report &&
			activeStep !== TicketStages.useCases &&
			activeStep !== TicketStages.testCases &&
			isChatOpen &&
			detailedViewTicketSummary === null,
		[activeStep, isChatOpen, detailedViewTicketSummary]
	);

	const handleTicketSave = () => {
		const ticketPermissions =
			activeTicket?.permissions.map((permission) => permission.permission) || [];
		const ticketStagesPermissions =
			activeTicket?.permissions.map((permission) => permission.ticketCurrentStep) || [];

		const isTicketReadMode = ticketPermissions.includes(SharePermissions.Read);
		const isTicketStageWithSave =
			ticketStagesPermissions.includes(TicketStages.general) ||
			ticketStagesPermissions.includes(TicketStages.notifications) ||
			ticketStagesPermissions.includes(null);
		const isTicketSaveAvailable = isTicketShared
			? !isTicketReadMode && isTicketStageWithSave
			: true;

		if (isTicketUpdate && isTicketSaveAvailable) {
			// if save is called right after local ticket was updated, then active ticket can not be updated in time. So we get active ticket directly from all tickets
			const currentTicket = tickets.find((ticket: TicketType) => ticket.id === activeTicket?.id);

			const updatedTicket = {
				...currentTicket,
				ticketSummaries: currentTicket?.ticketSummaries?.map((summary) => {
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
					const { isDeleted, ...rest } = summary;
					return rest;
				}),
			};

			dispatch(updateTicket(updatedTicket as TicketType)).then((res) => {
				dispatch(updateLocalTicket(res.payload));

				dispatch(setIsTicketUpdate(false));
				dispatch(resetTicketSave());

				successNotify(
					commonTranslate('errors.success'),
					commonTranslate('errors.autoSaveSuccessMessage')
				); 

				dispatchEvent('successfulSave', {});
			});
		} else {
			dispatch(resetTicketSave());
		}
	};

	const handleMessagesSet = (messages: Array<IMessage>) => {
		setMessages(messages);
		dispatch(updateLocalTicket({ id: activeTicket?.id, messages: messages }));
	};

	// if tickets is shared, automatically redirect to shared step
	useEffect(() => {
		if (activeTicket?.permissions?.length) {
			// if access given to all stages, do nothing.
			if (activeTicket.permissions.map((permission) => permission.ticketCurrentStep).includes(null))
				return;

			// redirect to stage with given access
			dispatch(
				setActiveStep(
					[...activeTicket.permissions.map((permission) => permission.ticketCurrentStep)].sort(
						(a, b) => (a === null ? 0 : a) - (b === null ? 0 : b)
					)[0]
				)
			);

			if (
				activeTicket.permissions
					.map((permission) => permission.ticketCurrentStep)
					.includes(TicketStages.general)
			) {
				// if access given to all subStages, do nothing.
				if (activeTicket.permissions.map((permission) => permission.subStageEnum).includes(null))
					return;
				// redirect to subStage with given access
				dispatch(
					setActiveSubStage(
						[...activeTicket.permissions.map((permission) => permission.subStageEnum)].sort(
							(a, b) => (a === null ? 0 : a) - (b === null ? 0 : b)
						)[0]
					)
				);
			}
		}
	}, [activeTicket?.id]);

	const renderStepContent = (ticketMessages: IMessage[]) => {
		switch (activeStep) {
			case TicketStages.general:
				return (
					<GeneralStep
						isChatLoading={isChatLoading}
						setIsChatLoading={setIsChatLoading}
						messages={ticketMessages}
						setMessages={(messages) => handleMessagesSet(messages)}
						ticketTitle={ticketTitle}
						setTitleError={setTitleError}
					/>
				);
			case TicketStages.useCases:
				return (
					<UsecasesStep
						messages={ticketMessages}
						setMessages={(messages) => setMessages(messages)}
						ticketTitle={ticketTitle}
						setTitleError={setTitleError}
					/>
				);
			case TicketStages.testCases:
				return (
					<TestCasesStep
						messages={ticketMessages}
						setMessages={(messages) => setMessages(messages)}
						ticketTitle={ticketTitle}
						setTitleError={setTitleError}
					/>
				);
			case TicketStages.notifications:
				return (
					<NotificationStep
						messages={ticketMessages}
						setMessages={(messages) => setMessages(messages)}
						description={description}
						ticketTitle={ticketTitle}
						setTitleError={setTitleError}
					/>
				);
			case TicketStages.report:
				return <ReportStep />;
			default:
				return null;
		}
	};

	return (
		<Box
			sx={{
				height: '100%',
				width: '100%',
				display: 'flex',
				flexDirection: { md: 'column', lg: 'row' },
				overflow: { md: 'auto', lg: 'hidden' },
				justifyContent: { xs: 'center', md: 'unset' },
				gap: '24px',
			}}
		>
			<Box
				sx={{
					display: {
						xs:
							(isMobile && contentView === 'RequestInfo') ||
							[TicketStages.report, TicketStages.useCases, TicketStages.testCases].includes(
								activeStep
							)
								? 'flex'
								: 'none',
						md: 'flex',
					},
					width: {
						xs: '99%',
						lg: isContentFullWidth ? '100%' : '60%',
						xl: isContentFullWidth ? '100%' : '70%',
					},
					height: '100%',
				}}
			>
				{isTicketLoading ? <Loader size={24} /> : renderStepContent(activeTicket?.messages || [])}
			</Box>

			{isChatWindowOpen ? (
				<FormProvider {...methods}>
					{
						<Box
							sx={{
								display: {
									xs: isMobile && contentView === 'Chat' ? 'flex' : 'none',
									md: 'flex',
								},
								width: { xs: '100%', md: '99%', lg: '40%', xl: '30%' },
								minWidth: { xs: '100%', md: '280px', lg: '320px' },
								flexShrink: { md: 1 },
							}}
						>
							<Chatbot
								isChatLoading={isChatLoading}
								messages={stageMessages}
								messageValue={messageValue}
								setMessageValue={setMessageValue}
								waitingProceeding={isWaitingProceeding}
							/>
						</Box>
					}
				</FormProvider>
			) : null}
		</Box>
	);
};
